import { createContext, useContext, useState, useEffect } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';
import { paymentApi } from '../services/paymentApi';

const AuthContext = createContext();

export const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [firstName, setFirstName] = useState(null);
  const [email, setEmail] = useState(null);
  const [lastFreeResearch, setLastFreeResearch] = useState('');
  const [sub, setSub] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscriptionUntil, setSubscriptionUntil] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState(null);
  const [subscriptionCanceledDate, setSubscriptionCanceledDate] = useState(null);
  const [credits, setCredits] = useState(0);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [weeklyResearchLimit] = useState(3); // Default subscription limit

  useEffect(() => {
    const loadUser = async () => {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        try {
          const session = await new Promise((resolve, reject) => {
            cognitoUser.getSession((err, session) => {
              if (err) {
                console.error('Session error:', err);
                reject(err);
              } else {
                resolve(session);
              }
            });
          });

          if (session) {
            const groups = session.getIdToken().payload['cognito:groups'] || [];
            const userEmail = session.getIdToken().payload.email;

            cognitoUser.getUserAttributes((err, attributes) => {
              if (err) {
                // console.error('Error loading user attributes:', err);
                return;
              }

              const userAttributes = {};
              attributes.forEach(attr => {
                userAttributes[attr.Name] = attr.Value;
              });

              setUser({
                ...cognitoUser,
                email: userEmail,
                groups,
                attributes: userAttributes
              });
              
              setFirstName(userAttributes['given_name'] || null);
              setEmail(userEmail);
              setLastFreeResearch(userAttributes['custom:last_free_research'] || '');
              setSub(userAttributes['sub'] || null);
              setCredits(Number(userAttributes['custom:credits'] || 0));
              
              // Set subscription related states
              const subUntil = userAttributes['custom:subscribe_end'] || null;
              setSubscriptionUntil(subUntil);
              
              const subType = userAttributes['custom:subscription'] || '';
              setSubscriptionType(subType);
              
              setSubscriptionCanceledDate(userAttributes['custom:subs_cancel_at'] || null);
              
              const now = new Date();
              const isActive = subUntil && new Date(subUntil) > now;
              setHasActiveSubscription(isActive && subType === 'SUB');
              
              setIsAdmin(userAttributes['custom:is_admin'] === 'true' || 
                        (groups && groups.includes('Admin')) || 
                        userEmail.endsWith('@instantflows.com'));
            });
          }
        } catch (error) {
          // console.error('Error loading user:', error);
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  // TODO - This is not used anywhere so should be removed?
  //const getRemainingWeeklyResearches = () => {
  //  if (!hasActiveSubscription) return 0;
    // For subscription users, we'll use credits to track their weekly researches
    // Each research costs 1 credit
  //  return Math.max(0, weeklyResearchLimit - (3 - credits));
  //};

  useEffect(() => {
    if (user?.email) {
      const isAdminEmail = user.email.toLowerCase().endsWith('@instantflows.com');
      const isAdminGroup = user.groups?.includes('Admin');
      const isAdminAttr = user.attributes?.['custom:is_admin'] === 'true';
      
      // Update isAdmin state based on any of the three conditions
      const newIsAdmin = isAdminEmail || isAdminGroup || isAdminAttr;
      setIsAdmin(newIsAdmin);

      // If user is admin by email but attribute not set, update it
      if (isAdminEmail && !isAdminAttr) {
        const cognitoUser = userPool.getCurrentUser();
        if (cognitoUser) {
          const attribute = new CognitoUserAttribute({
            Name: 'custom:is_admin',
            Value: 'true'
          });
          
          cognitoUser.updateAttributes([attribute], (err) => {
            if (err) {
              // console.error('Error updating admin attribute:', err);
            }
          });
        }
      }
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  const updateUserAttributes = async () => {
    try {
      const userDetails = await paymentApi.getUserDetails();
      
      // Update local state with the latest user details
      setCredits(parseInt(userDetails.credits || '0'));
      setSubscriptionCanceledDate(userDetails.subscriptionCanceledDate || null);
      setSubscriptionUntil(userDetails.subscriptionUntil || null);
      setSubscriptionType(userDetails.subscriptionType || null);
      setHasActiveSubscription(!!userDetails.subscriptionUntil && 
                               new Date(userDetails.subscriptionUntil) > new Date());
      
      return userDetails; // Explicitly return userDetails
    } catch (error) {
      // console.error('Failed to update user attributes:', error);
      throw error;
    }
  };

  const signUp = (email, password, firstName, lastName) => {
    return new Promise((resolve, reject) => {
      const attributeList = [
        new CognitoUserAttribute({ Name: 'email', Value: email }),
        new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
        new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
        new CognitoUserAttribute({ Name: 'custom:is_admin', Value: 'false' }),
        new CognitoUserAttribute({ Name: 'custom:last_free_research', Value: '' }),
        new CognitoUserAttribute({ Name: 'custom:subscribe_end', Value: '' }),
        new CognitoUserAttribute({ Name: 'custom:last_used_app', Value: '' }),
        new CognitoUserAttribute({ Name: 'custom:subscription', Value: 'FREE' }),
        new CognitoUserAttribute({ Name: 'custom:credits', Value: '1' })
      ];

      userPool.signUp(email, password, attributeList, null, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  const login = (email, password) => {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async session => {
          try {
            const groups = session.getIdToken().payload['cognito:groups'] || [];
            const userEmail = session.getIdToken().payload.email;
            
            await new Promise((resolve, reject) => {
              cognitoUser.getUserAttributes((err, attributes) => {
                if (err) {
                  reject(err);
                  return;
                }

                const userAttributes = {};
                attributes.forEach(attr => {
                  userAttributes[attr.Name] = attr.Value;
                });

                setUser({
                  ...cognitoUser,
                  email: userEmail,
                  groups,
                  attributes: userAttributes
                });
                
                setFirstName(userAttributes['given_name'] || null);
                setEmail(userEmail);
                setLastFreeResearch(userAttributes['custom:last_free_research'] || '');
                setSub(userAttributes['sub'] || null);
                setCredits(Number(userAttributes['custom:credits'] || 0));
                
                // Set subscription states on login
                const subUntil = userAttributes['custom:subscribe_end'] || null;
                setSubscriptionUntil(subUntil);
                
                const subType = userAttributes['custom:subscription'] || '';
                setSubscriptionType(subType);
                
                setSubscriptionCanceledDate(userAttributes['custom:subs_cancel_at'] || null);
                
                const now = new Date();
                const isActive = subUntil && new Date(subUntil) > now;
                setHasActiveSubscription(isActive && subType === 'SUB');
                
                resolve();
              });
            });
            resolve(session);
          } catch (error) {
            reject(error);
          }
        },
        onFailure: err => {
          reject(err);
        }
      });
    });
  };

  const logout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
      setUser(null);
      setIsAdmin(false);
      setFirstName(null);
      setEmail(null);
      setLastFreeResearch('');
      setSub(null);
      // Reset subscription states
      setSubscriptionUntil(null);
      setSubscriptionType(null);
      setCredits(0);
      setHasActiveSubscription(false);
    }
  };

  const verifyEmail = (email, code) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  // TODO - This is not used anywhere so should be removed?
  //const resendVerificationCode = email => {
  //  return new Promise((resolve, reject) => {
  //    const cognitoUser = new CognitoUser({
  //      Username: email,
  //      Pool: userPool
  //    });

  //    cognitoUser.resendConfirmationCode((err, result) => {
  //      if (err) {
  //        reject(err);
  //        return;
  //      }
  //      resolve(result);
  //    });
  //  });
  //};

  const forgotPassword = email => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: err => reject(err)
      });
    });
  };

  const confirmPassword = (email, code, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: err => reject(err)
      });
    });
  };

  const changePassword = (oldPassword, newPassword) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No user logged in'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err || !session.isValid()) {
          reject(new Error('User is not authenticated'));
          return;
        }

        cognitoUser.changePassword(oldPassword, newPassword, (err, result) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(result);
        });
      });
    });
  };
  const deleteAccount = async () => {
    try {
      await paymentApi.deleteAccount();
      
      // Clear local user data
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
      
      // Reset all state
      setUser(null);
      setFirstName(null);
      setEmail(null);
      setLastFreeResearch('');
      setSub(null);
      setIsAdmin(false);
      setSubscriptionUntil(null);
      setSubscriptionType(null);
      setSubscriptionCanceledDate(null);
      setCredits(0);
      setHasActiveSubscription(false);
      
      return true;
    } catch (error) {
      // console.error('Failed to delete account:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        firstName,
        email,
        sub,
        lastFreeResearch,
        loading,
        signUp,
        login,
        logout,
        verifyEmail,
        forgotPassword,
        confirmPassword,
        changePassword,
        subscriptionType,
        hasActiveSubscription,
        subscriptionUntil,
        subscriptionCanceledDate,
        credits,
        updateUserAttributes,
        deleteAccount,
        
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);